import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@features/ui';
import { FC } from 'react';

// Validation schema with Yup
const payoutSchema = yup.object({
  paymentMethod: yup.string().required('Please select a payment method'),
  amount: yup
    .number()
    .typeError('Amount must be a number')
    .positive('Amount must be greater than 0')
    .required('Please enter an amount')
    .when('$amounts', ([amounts], schema) => {
      if (amounts && amounts.fromAmount >= 1) {
        return schema
          .min(
            amounts.fromAmount,
            `Amount must be at least $${amounts.fromAmount}`
          )
          .max(amounts.toAmount, `Amount must be at most $${amounts.toAmount}`);
      }

      return schema;
    }),
});

export type PayoutFormData = yup.InferType<typeof payoutSchema>;

interface PayoutFormDataProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: PayoutFormData) => Promise<void>;
  fromAmount?: number;
  toAmount?: number;
}
export const PayoutDialog: FC<PayoutFormDataProps> = ({
  open,
  onOpenChange,
  onSubmit: onSubmitForm,
  fromAmount = 0,
  toAmount = 0,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<PayoutFormData>({
    resolver: yupResolver(payoutSchema),
    defaultValues: {
      paymentMethod: 'riseworks',
    },
    context: { amounts: { fromAmount, toAmount } },
  });

  const onSubmit = async (data: PayoutFormData) => {
    await onSubmitForm(data).catch((e) => console.log(e));
    onOpenChange(false);
    reset();
  };

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>Payout Request</DialogTitle>
          <DialogDescription>
            Complete the form to request your payout
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-6 mt-6" onSubmit={handleSubmit(onSubmit)}>
          {/* Payment Method - Radio Group */}
          <div className="space-y-2">
            <Label htmlFor="paymentMethod">Payment Method</Label>
            <Controller
              control={control}
              name="paymentMethod"
              render={({ field }) => (
                <RadioGroup
                  className="grid grid-cols-3 gap-4"
                  disabled={true}
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem id="riseworks" value="riseworks" />
                    <Label htmlFor="bankTransfer">Riseworks</Label>
                  </div>
                </RadioGroup>
              )}
            />
            {errors.paymentMethod && (
              <p className="text-sm text-red-500">
                {errors.paymentMethod.message}
              </p>
            )}
          </div>

          {/* Amount - Input */}
          <div className="space-y-2">
            <Label htmlFor="amount">
              Choose an amount from ${fromAmount} to ${toAmount}
            </Label>
            <Controller
              control={control}
              name="amount"
              render={({ field }) => (
                <Input
                  id="amount"
                  placeholder="Enter amount"
                  type="number"
                  {...field}
                />
              )}
            />
            {errors.amount && (
              <p className="text-sm text-red-500">{errors.amount.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <DialogFooter>
            <Button
              className="w-full"
              loading={isSubmitting}
              type="submit"
              variant="secondary"
            >
              Request Payout
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
